import type { SyntheticEvent } from 'react';

import type { SheetProps } from '@sravni/react-design-system/dist/types/components/Sheet';

export interface ModalConfig {
  component: React.FC<SheetProps & { data: any; onOverlayClick?(event: SyntheticEvent): void }>;
  props?: Record<string, any>;
  overlayClassName?: string;
  modalClassName?: string;
}

export interface ModalInstance extends ModalConfig {
  id: string;
  visible: boolean;
  resolve?: (value: any) => void;
  reject?: (reason?: any) => void;
}

export class ModalService {
  private modals: ModalInstance[] = [];
  private updateCallback: (() => void) | null = null;
  private nextId = 0;

  registerUpdateCallback(callback: () => void) {
    this.updateCallback = callback;
  }

  private generateId(): string {
    this.nextId += 1;
    return `modal-${this.nextId}`;
  }

  private notifyUpdate() {
    if (this.updateCallback) {
      this.updateCallback();
    }
  }

  open<T = any>(config: ModalConfig): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const modal: ModalInstance = {
        id: this.generateId(),
        visible: true,
        component: config.component,
        props: config.props || {},
        overlayClassName: config.overlayClassName,
        modalClassName: config.modalClassName,
        resolve,
        reject,
      };

      this.modals.push(modal);
      this.notifyUpdate();
    });
  }

  close(id: string, result?: any) {
    const modal = this.modals.find((m) => m.id === id);
    if (modal) {
      modal.visible = false;
      this.notifyUpdate(); // 👈 Немедленно обновляем видимость

      if (result instanceof Error) {
        modal.reject?.(result);
      } else {
        modal.resolve?.(result);
      }

      // Удаляем модалку после анимации закрытия
      setTimeout(() => {
        this.modals = this.modals.filter((m) => m.id !== id);
        this.notifyUpdate();
      }, 100);
    }
  }

  closeAll() {
    this.modals.forEach((modal) => {
      modal.reject?.(new Error('Modal closed by service'));
    });
    this.modals = [];
    this.notifyUpdate();
  }

  getModals(): ModalInstance[] {
    return this.modals;
  }
}

export const modalService = new ModalService();
